// Rep remittances: PURE logic (no supabase import, no I/O, no localStorage).
//
// A sales rep delivers product, collects payment from the shop and then owes the
// company that payment. The admin records "received from the sales rep" ONE
// invoice at a time in invoice_rep_remittances (migration 0033). This module
// holds every rule that does not need the network so it can be unit tested:
// the methods, input validation, eligibility, the amount, the cache mirror
// patches and the commission warning.
//
// SAFETY: the cached invoice is only ever a display mirror. The paid label this
// module writes can NEVER equal "Credit card" or "ACH" and never sets a
// paymentId / transactionId, so orderRemovals.refundPlan can never treat a rep
// remittance as a Square card / ACH payment and trigger an automatic refund.

import { orderRowsNewestFirst } from "./invoiceAdjustments";

export const REMIT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank transfer" },
];

export const REMIT_REFERENCE_MAX = 80;
export const REMIT_NOTE_MAX = 500;
export const REMIT_PAID_VIA = "rep_remittance";
export const REMIT_LABEL_PREFIX = "Received via sales rep";
export const REMIT_WARNING_CAP = 50;

const str = (v) => (v === null || v === undefined ? "" : String(v));
// "done" is a legacy synonym of "paid" everywhere in the app.
const isPaidStatus = (s) => s === "paid" || s === "done";

export function methodLabel(value) {
  const hit = REMIT_METHODS.find((m) => m.value === value);
  return hit ? hit.label : str(value);
}

// Local calendar date as YYYY-MM-DD.
export function todayIso(now) {
  const d = now instanceof Date ? now : new Date();
  const p = (n) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
}

function isRealIsoDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/**
 * Validate the admin's input. today (YYYY-MM-DD) is injectable for tests.
 * Returns { ok, errors: {field: message}, value: {method, reference, receivedOn, note} }.
 * receivedOn defaults to today when empty; a future date is refused.
 */
export function validateRemittanceInput({ method, reference, receivedOn, note } = {}, opts) {
  const errors = {};
  const today = (opts && opts.today) || todayIso();
  const m = str(method).trim();
  if (!m) errors.method = "Choose how the sales rep paid: cash, check, card or bank transfer.";
  else if (!REMIT_METHODS.some((x) => x.value === m)) errors.method = "Unknown payment method.";
  const ref = str(reference).trim();
  if (ref.length > REMIT_REFERENCE_MAX) errors.reference = "The reference is too long (max " + REMIT_REFERENCE_MAX + " characters).";
  const nt = str(note).trim();
  if (nt.length > REMIT_NOTE_MAX) errors.note = "The note is too long (max " + REMIT_NOTE_MAX + " characters).";
  const on = str(receivedOn).trim() || today;
  if (!isRealIsoDate(on)) errors.receivedOn = "Enter a valid received date.";
  else if (on > today) errors.receivedOn = "The received date cannot be in the future.";
  return {
    ok: Object.keys(errors).length === 0,
    errors,
    value: { method: m, reference: ref, receivedOn: on, note: nt },
  };
}

const isFieldRef = (ref) => str(ref).startsWith("INV-F");

/**
 * ONE shared predicate for "the shop paid us directly": status paid/done WITH a
 * paymentId (a Square card / ACH payment) and no rep remittance (neither an
 * active remittance row passed in nor a cache invoice mirrored as paid via a
 * remittance). A transactionId-only or reference-only record (an admin-recorded
 * offline payment) is NOT direct. Used by the Commission warning, the Commission
 * badge and the Orders "not yet received" filter so they always agree.
 */
export function isPaidDirectlyByShop(inv, activeRemittance) {
  if (!inv || activeRemittance) return false;
  if (!isPaidStatus(inv.status)) return false;
  if (inv.paidVia === REMIT_PAID_VIA) return false;
  // Same rule as paymentSourceOf === "shop": a Square payment id, or a card / ACH payment method.
  return !!inv.paymentId || inv.paidMethod === "Credit card" || inv.paidMethod === "ACH";
}

/**
 * How an already-paid invoice was paid, for the confirm in the admin form.
 *   "shop"  : paid through the shop / Square (a paymentId, or paidMethod Credit card / ACH)
 *   "other" : marked paid by the rep's toggle or an admin offline record (no paymentId)
 *   ""      : not paid, or paid by a rep remittance already
 */
export function paymentSourceOf(inv) {
  if (!inv || !isPaidStatus(inv.status) || inv.paidVia === REMIT_PAID_VIA) return "";
  if (inv.paymentId || inv.paidMethod === "Credit card" || inv.paidMethod === "ACH") return "shop";
  return "other";
}

export function activeRemittanceFor(list, invoiceId) {
  const id = str(invoiceId);
  if (!id || !Array.isArray(list)) return null;
  const active = list.filter((r) => r && r.status === "received" && str(r.invoice_id) === id);
  if (active.length === 0) return null;
  return active.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a));
}

/**
 * The amount to record: the invoice total of the NEWEST portal_orders row (the
 * same row getAllPortalOrders / dedupeOrderRows exposes). Returns a number >= 0
 * or null when it cannot be determined.
 */
export function remittanceAmount(freshOrderRows) {
  const rows = orderRowsNewestFirst(freshOrderRows);
  if (rows.length === 0) return null;
  const t = rows[0].total;
  if (t === null || t === undefined || t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

/**
 * Can this invoice be marked "received from the sales rep"?
 * invoice: { id, repCreated?, status?, source? } (the cached invoice or a
 * DB-derived stand-in). freshOrderRows: the portal_orders rows for the invoice
 * ref, read fresh. activeRemittance: the active remittance row or null.
 * Returns { ok, reason }.
 */
export function remittanceEligibility({ invoice, freshOrderRows, activeRemittance } = {}) {
  if (!invoice || !invoice.id) return { ok: false, reason: "No invoice." };
  const repCreated = !!invoice.repCreated || isFieldRef(invoice.id);
  if (!repCreated) return { ok: false, reason: "Only invoices created by a sales rep can be marked as received from the rep. Dealer orders are paid by the shop." };
  const rows = Array.isArray(freshOrderRows) ? freshOrderRows.filter(Boolean) : [];
  if (rows.length === 0) return { ok: false, reason: "This invoice was not found in the database yet, so it cannot be marked. Wait for it to sync, then try again." };
  if (str(invoice.status) === "cancelled" || rows.some((r) => r.status === "cancelled")) {
    return { ok: false, reason: "This invoice is cancelled." };
  }
  if (activeRemittance && activeRemittance.status === "received") {
    return { ok: false, reason: "Payment from the sales rep was already recorded for this invoice." };
  }
  return { ok: true, reason: "" };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-03-12" -> "12 Mar" (no timezone maths: it is a plain calendar date).
function shortDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(iso));
  if (!m) return "";
  return Number(m[3]) + " " + (MONTHS[Number(m[2]) - 1] || "");
}
// "2026-03-12" -> "Mar 12, 2026", the format SHARED_INVOICES.markPaid stamps.
function usDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(iso));
  if (!m) return "";
  return (MONTHS[Number(m[2]) - 1] || "") + " " + Number(m[3]) + ", " + m[1];
}

// Short method text: "Check #123", "Bank transfer ref X", "Cash".
function methodWithRef(rem) {
  const label = methodLabel(rem && rem.method);
  const ref = str(rem && rem.reference).trim();
  if (!ref) return label;
  return rem.method === "check" ? label + " " + (ref.startsWith("#") ? ref : "#" + ref) : label + " ref " + ref;
}

/** "Received via sales rep: Check #123, 12 Mar". */
export function remittancePaidLabel(rem) {
  if (!rem) return "";
  const when = shortDate(rem.received_on);
  return REMIT_LABEL_PREFIX + ": " + methodWithRef(rem) + (when ? ", " + when : "");
}

// The paidMethod string mirrored into the cache. Deliberately not "Credit card"
// or "ACH" (see the SAFETY note at the top).
export function remittanceCacheMethod(rem) {
  return REMIT_LABEL_PREFIX + " (" + methodLabel(rem && rem.method) + ")";
}

const PATCH_FIELDS = ["paidMethod", "paidDate", "paidAt", "receivedOn", "paidVia", "remittanceId", "remittanceMethod", "remittancePrevStatus", "remittancePrevPending", "remittanceKeptPayment"];

/**
 * Forward-only paid mirror of an ACTIVE remittance onto a cached invoice.
 * Returns { ok, changed, invoice, reason? }. The returned invoice is a shallow
 * copy (or the same object when nothing changed). Touches only status and the
 * payment fields: never items, total, shipping or notes. Refuses a cancelled
 * invoice, a non-active remittance, and an invoice already paid another way.
 */
export function cachePatchFromRemittance(cachedInvoice, rem) {
  const inv = cachedInvoice;
  if (!inv || !inv.id) return { ok: false, changed: false, invoice: inv, reason: "no cached invoice" };
  if (!rem || rem.status !== "received") return { ok: false, changed: false, invoice: inv, reason: "the remittance is not active" };
  if (str(inv.id) !== str(rem.invoice_id)) return { ok: false, changed: false, invoice: inv, reason: "the remittance belongs to another invoice" };
  if (inv.status === "cancelled") return { ok: false, changed: false, invoice: inv, reason: "the invoice is cancelled" };
  if (isPaidStatus(inv.status)) {
    if (inv.paidVia === REMIT_PAID_VIA && str(inv.remittanceId) === str(rem.id)) return { ok: true, changed: false, invoice: inv };
    if (inv.paidVia === REMIT_PAID_VIA) return { ok: false, changed: false, invoice: inv, reason: "the invoice is paid by another remittance; left unchanged" };
    // Paid through the shop / Square: never touched.
    if (paymentSourceOf(inv) === "shop") return { ok: false, changed: false, invoice: inv, reason: "the invoice is paid by the shop; left unchanged" };
    // Marked paid by the rep's toggle or an admin offline record: ADD the remittance fields only.
    // status, paidMethod, paidDate, paidAt, receivedOn, transactionId and every other payment field stay as they are.
    const kept = { ...inv };
    kept.paidVia = REMIT_PAID_VIA;
    kept.remittanceId = rem.id;
    kept.remittanceMethod = rem.method;
    kept.remittanceKeptPayment = true;
    return { ok: true, changed: true, invoice: kept };
  }
  const next = { ...inv };
  // The real prior status is recorded so a reversal restores exactly it.
  next.remittancePrevStatus = str(inv.status) || "outstanding";
  if (inv.status === "payment_pending") {
    next.remittancePrevPending = { method: inv.pendingMethod || null, date: inv.pendingDate || null, note: inv.pendingNote || null };
  }
  next.status = "paid";
  next.paidMethod = remittanceCacheMethod(rem);
  next.paidDate = usDate(rem.received_on) || inv.paidDate;
  next.paidAt = rem.received_on || null;
  next.receivedOn = rem.received_on || null;
  next.paidVia = REMIT_PAID_VIA;
  next.remittanceId = rem.id;
  next.remittanceMethod = rem.method;
  delete next.pendingMethod;
  delete next.pendingDate;
  return { ok: true, changed: true, invoice: next };
}

/**
 * Revert the mirror after a reversal. Only a cached invoice whose paidVia is
 * rep_remittance AND whose remittanceId matches rem.id is touched; anything paid
 * another way (or by another remittance) is left exactly as it is. The invoice
 * goes back to the unpaid status it had before. Returns { ok, changed, invoice, reason? }.
 */
export function cachePatchFromReversal(cachedInvoice, rem) {
  const inv = cachedInvoice;
  if (!inv || !inv.id) return { ok: false, changed: false, invoice: inv, reason: "no cached invoice" };
  if (!rem || rem.id === undefined || rem.id === null) return { ok: false, changed: false, invoice: inv, reason: "no remittance" };
  if (inv.paidVia !== REMIT_PAID_VIA || str(inv.remittanceId) !== str(rem.id)) {
    return { ok: true, changed: false, invoice: inv, reason: "the cached invoice is not paid by this remittance" };
  }
  if (inv.status === "cancelled") return { ok: true, changed: false, invoice: inv, reason: "the invoice is cancelled" };
  const next = { ...inv };
  if (inv.remittanceKeptPayment) {
    // The invoice was already paid (rep toggle / offline record): remove ONLY the remittance fields;
    // status, paidMethod, paidDate, paidAt, receivedOn, transactionId stay exactly as they were.
    ["paidVia", "remittanceId", "remittanceMethod", "remittanceKeptPayment"].forEach((k) => { delete next[k]; });
    return { ok: true, changed: true, invoice: next };
  }
  const prevRaw = str(inv.remittancePrevStatus);
  const prev = prevRaw && !isPaidStatus(prevRaw) && prevRaw !== "cancelled" ? prevRaw : "outstanding";
  next.status = prev;
  if (prev === "payment_pending" && inv.remittancePrevPending) {
    const p = inv.remittancePrevPending;
    if (p.method) next.pendingMethod = p.method;
    if (p.date) next.pendingDate = p.date;
    if (p.note) next.pendingNote = p.note;
  }
  PATCH_FIELDS.forEach((k) => { delete next[k]; });
  return { ok: true, changed: true, invoice: next };
}

/**
 * Warn-only list for the Commission tab: invoices that are rep-created, not
 * cancelled, have NO active remittance and are not known to be paid directly by
 * the shop. invoices: cached [{ id, customer, total, status, date, repCreated,
 * paidVia, paymentId, transactionId }]. remittances: rows (any status; only
 * active ones count). directPaidHints: optional array / Set of invoice ids known
 * to be paid directly. Returns { lines, count, truncated } with lines sorted by
 * invoice id and capped for display.
 */
export function commissionRemittanceWarning({ invoices, remittances, directPaidHints, cap } = {}) {
  const active = new Set((Array.isArray(remittances) ? remittances : [])
    .filter((r) => r && r.status === "received").map((r) => str(r.invoice_id)));
  const hints = new Set(Array.from(directPaidHints || []).map((x) => str(x)));
  const limit = Number.isFinite(cap) && cap > 0 ? cap : REMIT_WARNING_CAP;
  const seen = new Set();
  const all = [];
  (Array.isArray(invoices) ? invoices : []).forEach((inv) => {
    if (!inv || !inv.id) return;
    const id = str(inv.id);
    if (seen.has(id)) return;
    if (!(inv.repCreated || isFieldRef(id))) return;
    if (inv.status === "cancelled") return;
    if (active.has(id)) return;
    if (hints.has(id)) return;
    if (isPaidDirectlyByShop(inv)) return;
    seen.add(id);
    all.push({ invoiceId: id, customer: str(inv.customer), total: Number(inv.total) || 0, status: str(inv.status), date: str(inv.date) });
  });
  all.sort((a, b) => a.invoiceId.localeCompare(b.invoiceId, undefined, { numeric: true }));
  return { lines: all.slice(0, limit), count: all.length, truncated: all.length > limit, ids: all.map((l) => l.invoiceId) };
}

// ─── Stage 2: UI + guard helpers (all pure) ──────────────────────────────────

export const REMIT_ADJUST_BLOCK_MSG = "The sales rep already handed this payment in, so this invoice cannot be adjusted. Void it instead (with refund handling) if it must change. Nothing was changed.";

export function remittanceReadFailedMsg(err) {
  return "Whether the sales rep already handed this payment in could not be checked (" + (str(err) || "unknown error") + "). Nothing was changed. Try again.";
}

// Columns a REP may read of their own rows: never marked_by / marked_by_id / note /
// reversed_* (internal admin data).
export const REP_REMIT_COLUMNS = "id,invoice_id,rep_id,customer,amount,method,reference,received_on,status,created_at,updated_at";

export const isRepInvoice = (inv) => !!inv && !!inv.id && (!!inv.repCreated || isFieldRef(inv.id));

/** Set of invoice ids with an ACTIVE remittance. */
export function activeRemittanceIds(list) {
  const s = new Set();
  (Array.isArray(list) ? list : []).forEach((r) => { if (r && r.status === "received") s.add(str(r.invoice_id)); });
  return s;
}

/** Map invoice id -> the active remittance row (newest by id). */
export function activeRemittanceMap(list) {
  const m = new Map();
  (Array.isArray(list) ? list : []).forEach((r) => {
    if (!r || r.status !== "received") return;
    const k = str(r.invoice_id);
    const cur = m.get(k);
    if (!cur || Number(r.id) > Number(cur.id)) m.set(k, r);
  });
  return m;
}

function emptyBucket() { return { count: 0, total: 0, byMethod: {} }; }
function addTo(bucket, r) {
  const amt = Number(r.amount) || 0;
  bucket.count += 1;
  bucket.total = Math.round((bucket.total + amt) * 100) / 100;
  const k = str(r.method);
  const b = bucket.byMethod[k] || (bucket.byMethod[k] = { count: 0, total: 0 });
  b.count += 1;
  b.total = Math.round((b.total + amt) * 100) / 100;
}

/** Count and total received from reps, this month (by received_on) and all time, by method. */
export function remittanceSummary(list, now) {
  const month = todayIso(now).slice(0, 7);
  const out = { thisMonth: emptyBucket(), allTime: emptyBucket() };
  (Array.isArray(list) ? list : []).forEach((r) => {
    if (!r || r.status !== "received") return;
    addTo(out.allTime, r);
    if (str(r.received_on).slice(0, 7) === month) addTo(out.thisMonth, r);
  });
  return out;
}

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtRemitMoney = money;

/** "3 received, $1,200.00 (Cash 1 $200.00, Check 2 $1,000.00)". */
export function remittanceBucketText(b) {
  if (!b || !b.count) return "none";
  const parts = REMIT_METHODS.filter((m) => b.byMethod[m.value]).map((m) => m.label + " " + b.byMethod[m.value].count + " " + money(b.byMethod[m.value].total));
  return b.count + " received, " + money(b.total) + (parts.length ? " (" + parts.join(", ") + ")" : "");
}

// Pill colours: violet, deliberately unlike the green "Paid" pill.
const REMIT_PILL = { bg: "#EDE9FE", fg: "#5B21B6", border: "#C4B5FD" };
export function remittancePill(rem) {
  return {
    label: "Paid via rep: " + methodLabel(rem && rem.method),
    style: { display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: REMIT_PILL.bg, color: REMIT_PILL.fg, border: "1px solid " + REMIT_PILL.border, whiteSpace: "nowrap" },
  };
}

export const ORDERS_REMIT_FILTERS = [
  { value: "", label: "All payment sources" },
  { value: "rep_received", label: "Paid to us by rep" },
  { value: "rep_not_received", label: "Rep invoices not yet received from rep" },
];

/** Orders-tab filter. mode: "" | "rep_received" | "rep_not_received". */
export function matchesRemitFilter(inv, activeIds, mode) {
  if (!mode) return true;
  if (!inv || !inv.id) return false;
  const has = activeIds && activeIds.has(str(inv.id));
  if (mode === "rep_received") return !!has;
  if (mode === "rep_not_received") return isRepInvoice(inv) && inv.status !== "cancelled" && !has && !isPaidDirectlyByShop(inv);
  return true;
}

/** Commission-row badge: null when the invoice is not rep-created or the read failed. */
export function commissionRemitBadge(inv, remittances, readOk) {
  if (!isRepInvoice(inv) || readOk === false) return null;
  const rem = activeRemittanceFor(remittances, inv.id);
  if (rem) return { kind: "received", label: "Received from rep (" + methodLabel(rem.method) + ")" };
  if (isPaidDirectlyByShop(inv)) return { kind: "paid_by_shop", label: "Paid by shop" };
  return { kind: "not_received", label: "Not received from rep" };
}

/**
 * Gate for the ONE shared Mark Paid modal. Warn only, never a block.
 * invoices: the invoices being paid (cached shape). remittances: rows or null.
 * readOk false = the remittance read failed. Returns
 * { kind: "none"|"warn"|"readfailed", needsAck, lines, count, truncated }.
 */
export function commissionPayoutGate({ invoices, remittances, readOk, directPaidHints, cap } = {}) {
  const w = commissionRemittanceWarning({ invoices, remittances: readOk === false ? [] : remittances, directPaidHints, cap });
  if (readOk === false) {
    return { kind: w.count > 0 ? "readfailed" : "none", needsAck: w.count > 0, lines: w.lines, count: w.count, truncated: w.truncated, ids: w.ids };
  }
  if (w.count === 0) return { kind: "none", needsAck: false, lines: [], count: 0, truncated: false, ids: [] };
  return { kind: "warn", needsAck: true, lines: w.lines, count: w.count, truncated: w.truncated, ids: w.ids };
}

/**
 * The acknowledgement tick belongs to ONE specific warning: the sorted list of
 * warned invoice ids (the FULL list, not the capped display list), the gate kind
 * and the pay-through date. Storing the tick as this key means it invalidates
 * itself whenever any of those change, so an old tick can never pre-approve a
 * different payout. "" = no acknowledgement is needed / possible.
 */
export function gateAckKey(gate, payThrough) {
  if (!gate || !gate.needsAck) return "";
  const ids = (Array.isArray(gate.ids) ? gate.ids : (gate.lines || []).map((l) => l.invoiceId)).map((x) => str(x)).slice().sort();
  return str(gate.kind) + "|" + str(payThrough) + "|" + ids.length + "|" + ids.join(",");
}

/** Text of the void warning, "" when there is nothing to warn about. */
export function voidRemittanceWarning(rem) {
  if (!rem) return "";
  return "Money was already received from the sales rep for this invoice (" + methodWithRef(rem) + ", " + money(rem.amount) + ", " + (shortDate(rem.received_on) || "date unknown") + "). Voiding does NOT refund it: handle the repayment manually.";
}

/** What the void / approve modals need from a strict read: { needsAck, tone, text }. */
export function voidRemittanceGate(strictRes) {
  if (!strictRes) return { needsAck: false, tone: "none", text: "" };
  if (strictRes.ok === false) {
    return { needsAck: true, tone: "error", text: "Whether money was already received from the sales rep could not be checked (" + (str(strictRes.error) || "unknown error") + "). Voiding does NOT refund a payment the rep handed in. Tick to continue anyway." };
  }
  if (strictRes.remittance) return { needsAck: true, tone: "warn", text: voidRemittanceWarning(strictRes.remittance) };
  return { needsAck: false, tone: "none", text: "" };
}

/** Only these fields of a remittance row are shown to a rep. */
export function repVisibleRemittance(rem) {
  if (!rem || rem.status !== "received") return null;
  return {
    id: rem.id, invoiceId: str(rem.invoice_id), method: rem.method, methodLabel: methodLabel(rem.method),
    reference: str(rem.reference).trim(), receivedOn: str(rem.received_on), date: usDate(rem.received_on),
  };
}

/** "Check #123, Mar 12, 2026" for the rep's own invoice. */
export function repReceivedText(rem) {
  const v = rem && rem.methodLabel ? rem : repVisibleRemittance(rem);
  if (!v) return "";
  const r = methodWithRef({ method: v.method, reference: v.reference });
  return r + (v.date ? ", " + v.date : "");
}

export const REP_TOGGLE_REFUSAL = "The company already received this payment from you, so it cannot be changed here. Ask the admin if it was recorded by mistake.";

/** True when an invoice is paid because the company recorded a rep remittance. */
export function isCompanyReceived(inv, activeRemittance) {
  return !!activeRemittance || (!!inv && inv.paidVia === REMIT_PAID_VIA);
}

/** Remittance rows oldest first, so a reversal of an older row is applied before a newer active row. */
export function sortRemittanceRowsForApply(rows) {
  return (Array.isArray(rows) ? rows.filter(Boolean) : []).slice().sort((a, b) => Number(a.id) - Number(b.id));
}

/**
 * The admin modal may be handed a copy of an invoice. When either side carries a
 * rep remittance, take the payment state from the live cache object so the header
 * pill and the paid block follow a mark / reverse. Otherwise the prop is untouched.
 */
export function overlayRemittanceState(prop, live) {
  if (!prop || !live || prop === live) return prop;
  if (live.paidVia !== REMIT_PAID_VIA && prop.paidVia !== REMIT_PAID_VIA) return prop;
  const o = { ...prop };
  ["status", "paidMethod", "paidDate", "paidAt", "receivedOn", "paidVia", "remittanceId", "remittanceMethod", "remittanceKeptPayment", "pendingMethod", "pendingDate", "pendingNote"].forEach((k) => {
    if (live[k] === undefined) delete o[k]; else o[k] = live[k];
  });
  return o;
}

/**
 * "shop" when the invoice was paid through the shop / Square (needs an explicit
 * confirm before recording a rep remittance), "other" when it was marked paid by
 * the rep's toggle or an admin offline record (a neutral note only; the mirror
 * adds the remittance fields without changing its payment fields), "" otherwise.
 */
export function paidAnotherWay(inv) {
  return paymentSourceOf(inv);
}

/** Copy the fields of next onto the live object IN PLACE (deleting removed keys) so every holder of the reference sees it. */
export function assignInPlace(target, next) {
  Object.keys(target).forEach((k) => { if (!(k in next)) delete target[k]; });
  Object.assign(target, next);
  return target;
}

/**
 * Warning for the Orders-tab rep request cards (display only). kind: "void" | "adjust".
 * Returns "" when there is no active remittance.
 */
export function requestRemittanceWarning(rem, kind) {
  if (!rem) return "";
  const what = "Money was already received from the sales rep for this invoice (" + methodWithRef(rem) + ", " + money(rem.amount) + ", " + (shortDate(rem.received_on) || "date unknown") + ").";
  if (kind === "adjust") return what + " Approving this adjustment changes the invoice total but does NOT change what was received: settle any difference with the rep manually.";
  return what + " Approving this void does NOT refund it: handle the repayment manually.";
}

// ─── Review-fix helpers (R2, R7) ─────────────────────────────────────────────

/**
 * Gate for the Reverse action: was commission for this invoice already marked
 * Paid? state: the fresh read result { ok, paid, rowCount, correctionCount, error }.
 * Returns { blocked, needsAck, tone, text }. A failed read BLOCKS (never reverse blind).
 */
export function reverseCommissionGate(state) {
  if (!state) return { blocked: true, needsAck: false, tone: "loading", text: "" };
  if (state.ok === false) {
    return { blocked: true, needsAck: false, tone: "error", text: "Whether commission for this invoice was already paid could not be checked (" + (str(state.error) || "unknown error") + "). Reversing is blocked until the check works. Retry." };
  }
  if (state.paid) {
    return { blocked: false, needsAck: true, tone: "warn", text: "Commission for this invoice is already marked Paid. Reversing this payment does NOT change commission already paid: review the Commission tab (the invoice will show as unpaid again while its commission stays paid)." };
  }
  return { blocked: false, needsAck: false, tone: "none", text: "" };
}

/**
 * Compare the amount the admin was shown with a fresh read taken at Confirm time.
 * shown / fresh: numbers or null. Returns "" when they agree, else a message
 * with the new amount so the admin re-confirms.
 */
export function amountChangedMessage(shown, fresh) {
  const a = shown === null || shown === undefined ? null : Math.round(Number(shown) * 100);
  const b = fresh === null || fresh === undefined ? null : Math.round(Number(fresh) * 100);
  if (a === b) return "";
  if (b === null) return "The invoice amount can no longer be read (it was " + money(shown) + "). Nothing was recorded. Reopen this invoice and try again.";
  return "The invoice amount changed while this form was open: it is now " + money(fresh) + (a === null ? "" : " (it was " + money(shown) + ")") + ". Nothing was recorded. Review the amount and press Confirm again.";
}
